import type { Almacen, Expediente } from '../types'

export interface NodoCaja {
  id: string
  nombre: string
  capacidad: number
  expedientes: Expediente[]
}

export interface NodoEstante {
  id: string
  nombre: string
  cajas: NodoCaja[]
}

export interface NodoPasillo {
  id: string
  nombre: string
  estantes: NodoEstante[]
}

export interface NodoAlmacen {
  id: string
  nombre: string
  descripcion: string
  pasillos: NodoPasillo[]
}

/** Cruza la estructura física configurada con los expedientes resguardados. */
export function mapaDeAlmacenes(
  almacenes: Almacen[],
  expedientes: Expediente[],
  sucursalId: string,
): NodoAlmacen[] {
  const delaSucursal = expedientes.filter((e) => e.sucursalId === sucursalId)

  return almacenes
    .filter((a) => a.sucursalId === sucursalId)
    .map((almacen) => ({
      id: almacen.id,
      nombre: almacen.nombre,
      descripcion: almacen.descripcion,
      pasillos: almacen.pasillos.map((pasillo) => ({
        id: pasillo.id,
        nombre: pasillo.nombre,
        estantes: pasillo.estantes.map((estante) => ({
          id: estante.id,
          nombre: estante.nombre,
          cajas: estante.cajas.map((caja) => ({
            id: caja.id,
            nombre: caja.nombre,
            capacidad: caja.capacidad,
            expedientes: delaSucursal.filter(
              (e) =>
                e.ubicacion.almacen === almacen.nombre &&
                e.ubicacion.pasillo === pasillo.nombre &&
                e.ubicacion.estante === estante.nombre &&
                e.ubicacion.caja === caja.nombre,
            ),
          })),
        })),
      })),
    }))
}

export function contarCajas(almacen: Almacen): number {
  return almacen.pasillos.reduce(
    (total, p) => total + p.estantes.reduce((sub, e) => sub + e.cajas.length, 0),
    0,
  )
}

export function expedientesEnAlmacen(almacen: Almacen, expedientes: Expediente[]): number {
  return expedientes.filter(
    (e) => e.sucursalId === almacen.sucursalId && e.ubicacion.almacen === almacen.nombre,
  ).length
}

export type NivelOcupacion = 'vacia' | 'disponible' | 'casi' | 'llena'

export function nivelOcupacion(ocupado: number, capacidad: number): NivelOcupacion {
  if (ocupado === 0) return 'vacia'
  if (ocupado >= capacidad) return 'llena'
  if (ocupado / capacidad >= 0.75) return 'casi'
  return 'disponible'
}

export const ESTILO_OCUPACION: Record<
  NivelOcupacion,
  { barra: string; texto: string; etiqueta: string }
> = {
  vacia: { barra: 'bg-gris-300', texto: 'text-gris-400', etiqueta: 'Vacía' },
  disponible: { barra: 'bg-marino-600', texto: 'text-gris-500', etiqueta: 'Con espacio' },
  casi: { barra: 'bg-ambar-400', texto: 'text-ambar-700', etiqueta: 'Casi llena' },
  llena: { barra: 'bg-peligro-500', texto: 'text-peligro-600', etiqueta: 'Llena' },
}

export function rutaUbicacion(exp: Expediente): string {
  const { almacen, pasillo, estante, caja } = exp.ubicacion
  return `${almacen} · ${pasillo} · ${estante} · ${caja}`
}
